import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useConfig } from 'hooks/use-config';
import { useWhiteLabelStore } from 'state/store/white-label';
import { initializeWhiteLabelSettings } from 'services/white-label-service';

/**
 * ThemeProvider Component
 *
 * A context provider that manages theme state for your application,
 * supporting light, dark, and system themes with localStorage persistence.
 *
 * Features:
 * - Theme state management (light, dark, system)
 * - Persistent theme selection using localStorage
 * - System theme detection and synchronization
 * - Automatic application of theme classes to the document root
 * - Context API for consuming theme state and functions throughout the app
 *
 * Props:
 * @param {ReactNode} children - Child components that will have access to the theme context
 * @param {Theme} [defaultTheme='system'] - The default theme to use if none is stored
 * @param {string} [storageKey='theme'] - The localStorage key used to persist theme preference
 *
 * @returns {JSX.Element} A context provider that supplies theme values to children
 *
 * @example
 * // Basic usage at the root of your app
 * <ThemeProvider defaultTheme="system">
 *   <App />
 * </ThemeProvider>
 *
 * // Consuming the theme context in a component
 * function ThemeToggle() {
 *   const { selectedTheme, setTheme } = useTheme();
 *
 *   return (
 *     <button onClick={() => setTheme(selectedTheme === 'light' ? 'dark' : 'light')}>
 *       Toggle theme
 *     </button>
 *   );
 * }
 */

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Exclude<Theme, 'system'>;
  selectedTheme: Theme;
  resolvedTheme: Exclude<Theme, 'system'>;
  setTheme: (theme: Theme) => void;
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  applyColorTheme: (color: string) => void;
  logoUrl: string | null;
  isWhiteLabelLoading: boolean;
};

const initialState: ThemeProviderState = {
  theme: 'light',
  selectedTheme: 'system',
  resolvedTheme: 'light',
  setTheme: () => null,
  primaryColor: '#2196e4',
  setPrimaryColor: () => null,
  applyColorTheme: () => null,
  logoUrl: null,
  isWhiteLabelLoading: false,
};

const ThemeContext = createContext<ThemeProviderState>(initialState);

// Default primary color
const DEFAULT_PRIMARY_COLOR = '#2196e4';

// Color theme utilities
const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const hslToString = (h: number, s: number, l: number): string => {
  return `${h}, ${s}%, ${l}%`;
};

const hslToRgb = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;

  if (0 <= h && h < 60) {
    r = c; g = x; b = 0;
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0;
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x;
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c;
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c;
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `${r}, ${g}, ${b}`;
};

const generatePrimaryPalette = (baseHex: string) => {
  const { h, s, l } = hexToHsl(baseHex);

  return {
    light: hslToString(h, Math.max(20, s + 10), Math.min(95, l + 38)),
    25: hslToString(h, Math.max(20, s + 10), Math.min(91, l + 34)),
    shadow: hslToString(h, s, l),
    accent: hslToString(h, Math.max(5, s - 15), Math.max(12, l - 42)),
    50: hslToString(h, Math.min(100, s + 40), Math.min(95, l + 30)),
    100: hslToString(h, Math.min(100, s + 30), Math.min(90, l + 25)),
    200: hslToString(h, Math.min(100, s + 20), Math.min(85, l + 20)),
    300: hslToString(h, Math.max(30, s - 10), Math.min(80, l + 15)),
    400: hslToString(h, Math.max(20, s - 15), Math.min(70, l + 5)),
    500: hslToString(h, s, l),
    600: hslToString(h, s, Math.max(40, l - 10)),
    700: hslToString(h, Math.min(100, s + 5), Math.max(30, l - 20)),
    800: hslToString(h, Math.min(100, s + 10), Math.max(20, l - 30)),
    900: hslToString(h, Math.min(100, s + 15), Math.max(15, l - 40)),
    foreground: hslToString(h, Math.min(100, s + 15), Math.max(15, l - 40)),
  };
};

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'theme',
}: Readonly<ThemeProviderProps>) {
  const getSystemTheme = (): Exclude<Theme, 'system'> =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

  const [selectedTheme, setSelectedTheme] = useState<Theme>(
    () => {
      const savedTheme = localStorage.getItem(storageKey);

      if (savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'system') {
        return savedTheme;
      }

      return defaultTheme;
    }
  );
  const [resolvedTheme, setResolvedTheme] = useState<Exclude<Theme, 'system'>>(
    () => (selectedTheme === 'system' ? getSystemTheme() : selectedTheme)
  );

  const [primaryColor, setPrimaryColor] = useState<string>(DEFAULT_PRIMARY_COLOR);

  // White-label integration
  const { configurationSettings } = useConfig();
  const {
    settings: whiteLabelSettings,
    isLoading: isWhiteLabelLoading,
    setWhiteLabelSettings,
    setLoading: setWhiteLabelLoading,
    setError: setWhiteLabelError
  } = useWhiteLabelStore();

  // Apply color theme to CSS variables
  const applyColorTheme = (colorHex: string) => {
    const palette = generatePrimaryPalette(colorHex);
    const { h, s, l } = hexToHsl(colorHex);
    const root = document.documentElement;

    // Update CSS variables for both light and dark modes
    root.style.setProperty('--primary-light', palette.light);
    root.style.setProperty('--primary-25', palette[25]);
    root.style.setProperty('--primary-shadow', palette.shadow);
    root.style.setProperty('--primary-shadow-rgb', hslToRgb(h, s, l));
    root.style.setProperty('--primary-accent', palette.accent);
    root.style.setProperty('--primary-50', palette[50]);
    root.style.setProperty('--primary-100', palette[100]);
    root.style.setProperty('--primary-200', palette[200]);
    root.style.setProperty('--primary-300', palette[300]);
    root.style.setProperty('--primary-400', palette[400]);
    root.style.setProperty('--primary-500', palette[500]);
    root.style.setProperty('--primary-600', palette[600]);
    root.style.setProperty('--primary-700', palette[700]);
    root.style.setProperty('--primary-800', palette[800]);
    root.style.setProperty('--primary-900', palette[900]);
    // Keep primary-foreground white in both light and dark mode for button text
    root.style.setProperty('--primary-foreground', '0 0% 100%');
    root.style.setProperty('--ring', palette[500]);
  };

  // Initialize white-label settings
  useEffect(() => {
    if (configurationSettings && !whiteLabelSettings && !isWhiteLabelLoading) {
      initializeWhiteLabelSettings(
        configurationSettings,
        setWhiteLabelSettings,
        setWhiteLabelLoading,
        setWhiteLabelError
      );
    }
  }, [configurationSettings, whiteLabelSettings, isWhiteLabelLoading, setWhiteLabelSettings, setWhiteLabelLoading, setWhiteLabelError]);

  // Update primary color when white-label settings are loaded
  useEffect(() => {
    if (whiteLabelSettings?.themeCustomColor) {
      // const customColor = whiteLabelSettings.themeCustomColor;
      const customColor = DEFAULT_PRIMARY_COLOR;
      // Only update if it's different from current color and not already saved in localStorage
      const savedColor = localStorage.getItem('theme-primary-color');
      if (savedColor !== customColor) {
        setPrimaryColor(customColor);
        localStorage.setItem('theme-primary-color', customColor);
      }
    }
  }, [whiteLabelSettings]);

  // Apply primary color theme on mount and when primaryColor changes
  useEffect(() => {
    applyColorTheme(primaryColor);
  }, [primaryColor]);

  useEffect(() => {
    const root = window.document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const applyThemeClass = (nextTheme: Theme) => {
      const activeTheme = nextTheme === 'system'
        ? (mediaQuery.matches ? 'dark' : 'light')
        : nextTheme;

      root.classList.remove('light', 'dark');
      root.classList.add(activeTheme);
      setResolvedTheme(activeTheme);
    };

    applyThemeClass(selectedTheme);

    if (selectedTheme !== 'system') {
      return;
    }

    const handleSystemThemeChange = () => {
      applyThemeClass('system');
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [selectedTheme]);

  const value = useMemo(
    () => ({
      theme: resolvedTheme,
      selectedTheme,
      resolvedTheme,
      setTheme: (theme: Theme) => {
        localStorage.setItem(storageKey, theme);
        setSelectedTheme(theme);
      },
      primaryColor,
      setPrimaryColor: (color: string) => {
        localStorage.setItem('theme-primary-color', color);
        setPrimaryColor(color);
      },
      applyColorTheme,
      logoUrl: whiteLabelSettings?.logoUrl || null,
      isWhiteLabelLoading,
    }),
    [storageKey, selectedTheme, resolvedTheme, primaryColor, whiteLabelSettings?.logoUrl, isWhiteLabelLoading]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
